import React from "react";
import { BulkUpload } from "../../../../Entities/BulkUpload";
import { Flex, Tag, Typography, Divider } from "antd";
import {
  FileZipOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { capitalizeFirstLetter } from "../../../../Utils/generic-functions";
import { CHART_COLORS } from "../StatsAndAnalytics/utils/chartColors";
import dayjs from "dayjs";

interface BulkUploadCardProps {
  bulkUpload: BulkUpload;
  isActive?: boolean;
  onClick?: () => void;
}

const BulkUploadCard: React.FC<BulkUploadCardProps> = ({
  bulkUpload,
  isActive = false,
  onClick,
}) => {
  const fileName = bulkUpload?.zipFilePath.split("/").pop() || "Unknown";
  const totalFiles = bulkUpload?.totalNoOfFiles ?? 0;
  const completedFiles = bulkUpload?.noOfFilesCompleted ?? 0;
  const failedFiles = bulkUpload?.noOfFilesFailed ?? 0;

  const processedCount = completedFiles + failedFiles;
  const isAnyPendingLeft = totalFiles > 0 && totalFiles > processedCount;

  const formatZipFileName = (fileName: string) => {
    if (bulkUpload.source !== "default") {
      return fileName;
    }
    const lastDot = fileName.lastIndexOf(".");
    if (lastDot === -1) return fileName;
    const baseName = fileName.slice(0, lastDot);
    const ext = fileName.slice(lastDot);
    const lastUnderscore = baseName.lastIndexOf("_");
    if (lastUnderscore === -1) return fileName;
    return baseName.slice(0, lastUnderscore) + ext;
  };

  return (
    <section
      className={`bulk-upload-card ${isActive ? "active" : ""}`}
      onClick={onClick}
    >
      {/* Header Section */}
      <div className="bulk-upload-card__header">
        <Flex align="center" gap={8}>
          <div className="bulk-upload-card__icon">
            <FileZipOutlined />
          </div>
          <Typography.Text
            strong
            className="bulk-upload-card__filename"
            title={fileName}
          >
            {formatZipFileName(fileName)}
          </Typography.Text>
        </Flex>
      </div>

      {/* Progress Section */}
      {isAnyPendingLeft && (
        <div className="bulk-upload-card__progress">
          <div className="indeterminate-progress" />
        </div>
      )}

      {/* Stats Section */}
      <div className="bulk-upload-card__stats">
        <Tag
          color={CHART_COLORS.primary}
          icon={<FileZipOutlined />}
          className="bulk-upload-card__stat-tag"
        >
          {totalFiles} Total
        </Tag>
        <Tag
          color={CHART_COLORS.success}
          icon={<CheckCircleOutlined />}
          className="bulk-upload-card__stat-tag"
        >
          {completedFiles} Done
        </Tag>

        <Tag
          color={CHART_COLORS.error}
          icon={<CloseCircleOutlined />}
          className="bulk-upload-card__stat-tag"
        >
          {failedFiles} Failed
        </Tag>
      </div>

      <Divider style={{ margin: "0" }} />

      {/* Footer Section */}
      <div className="bulk-upload-card__footer">
        <Flex vertical gap={6}>
          <Flex align="center" gap={6}>
            <ClockCircleOutlined className="bulk-upload-card__footer-icon" />
            <Typography.Text
              type="secondary"
              className="bulk-upload-card__footer-text"
            >
              {dayjs(bulkUpload?.createdAt).format("DD MMM YYYY, hh:mm A")}
            </Typography.Text>
          </Flex>
          <Flex align="center" gap={6}>
            <UserOutlined className="bulk-upload-card__footer-icon" />
            <Typography.Text
              type="secondary"
              className="bulk-upload-card__footer-text"
            >
              {capitalizeFirstLetter(bulkUpload?.uploadedBy.name)}
            </Typography.Text>
          </Flex>
        </Flex>
      </div>
    </section>
  );
};

export default BulkUploadCard;
